import { AuditResourceType } from '_lib/api';
import { BlockedActionMode } from '_lib/auth/rbac';
import { Ivim, ivimType } from '_store/ivim/ivimsTypes';
import {
  mapPictogramText,
  mapServiceCategoryCode,
  mapServiceCategorySubCode,
} from '_store/ivim/utils';
import { useLocalization } from '@fluent/react';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faClone, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardContent, Grid, Tooltip, Typography } from '@material-ui/core';
import { blue, red } from '@material-ui/core/colors';
import React, { FC } from 'react';

import { PictogramFullID } from '../../../../icons/ivim/PictogramFulID';
import PictogramImage from '../../../../icons/ivim/PictogramImage';
import { Accordion } from "../../../../items/Accordion";
import AuditHistorySection from '../../../../items/AuditHistory';
import { RoleGuard } from '../../../../items/RoleGuard';
import { RsusMetaData } from '../../../Device/RsusMetaData';
import styles from './styles';

export interface CardDetailsProps {
  ivim: Ivim;
  onUpdate?: () => void;
  onDelete?: () => void;
  showButtons?: boolean;
  isActive?: boolean;
  onDisable?: () => void;
  onEnable?: () => void;
  isDisabled?: boolean;
  onClone?: () => void;
  writeAllowed?: boolean;
  blockedActionMode?: BlockedActionMode;
  blockedMessage?: string;
}

const CardDetails: FC<CardDetailsProps> = ({
  ivim,
  onUpdate,
  onDelete,
  showButtons,
  isActive = true,
  onDisable,
  onEnable,
  isDisabled = false,
  onClone,
  writeAllowed = true,
  blockedActionMode = 'disable',
  blockedMessage = '',
}) => {
  const classes = styles();
  const { l10n } = useLocalization();

  const mapIvimType = (type: number) => {
    const matchedType = ivimType().find((t) => t.value === type);
    if (matchedType) {
      return matchedType.label;
    }
    return '-';
  };

  return (
    <CardContent className={classes.detailStyle}>
      <Grid container spacing={1} style={{ marginTop: 10 }}>
        <Grid item xs={12}>
          <Typography component={'span'} variant={'body1'} paragraph>
            {l10n.getString('forms-ivim-detail-header')}:
          </Typography>
        </Grid>
        <Grid container spacing={1} style={{ marginTop: 10 }}>
          <Grid item xs={12} className={classes.gridVertical}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-id')} {`\n`}
              <strong>{ivim.id}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-sp-country-code')}{' '}
              <strong>{ivim?.countryCode ?? '-'}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-sp-country-id')}{' '}
              <strong>{ivim?.providerIdentifier ?? '-'}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-type')} <strong>{mapIvimType(ivim?.iviType)}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-valid-from')} {`\n`}{' '}
              <strong>{new Date(ivim.createdAt || ivim.starts).toLocaleString()}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-valid-to')} {`\n`}
              <strong>{new Date(ivim.ends).toLocaleString()}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-reference-point')} {`\n`}
              <strong>
                ({ivim.referencePoint.coordinates[1]}, {ivim.referencePoint.coordinates[0]})
              </strong>
            </Typography>
            {ivim.metadata.freeText?.length > 0 &&
              ivim.metadata.freeText.map((text, index) => (
                <Grid container spacing={1} style={{ marginBottom: 10 }} key={index}>
                  <Grid item xs={6}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-text')} {`\n`}
                      <strong>{text.value}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} style={{ paddingLeft: 10 }}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-language-text')} {`\n`}
                      <strong>{text.language ?? '-'}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            {ivim.metadata.extraText?.length > 0 &&
              ivim.metadata.extraText.map((text, index) => (
                <Grid container spacing={1} style={{ marginBottom: 10 }} key={index}>
                  <Grid item xs={6}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-extra-text')} {`\n`}
                      <strong>{text.value}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} style={{ paddingLeft: 10 }}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-language-text')} {`\n`}
                      <strong>{text.language ?? '-'}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            <PictogramImage
              pictogramNature={ivim?.pictogramNature}
              pictogramSerialNumber={ivim?.pictogramSerialNumber}
              serviceSubCategoryCode={ivim?.serviceSubCategoryCode}
            />

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-pictogram-code')} {`\n`}
              <strong>
                <PictogramFullID
                  serviceCategoryCode={ivim?.serviceCategoryCode}
                  serviceSubCategoryCode={ivim?.serviceSubCategoryCode}
                  pictogramCategoryCode={ivim?.pictogramNature}
                  pictogramSerialNumber={ivim?.pictogramSerialNumber}
                />
              </strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-ser-cat-cod')} {`\n`}
              <strong>{mapServiceCategoryCode(ivim?.serviceCategoryCode, l10n)}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-ser-sub-cat-cod')} {`\n`}
              <strong>{mapServiceCategorySubCode(ivim?.serviceSubCategoryCode, l10n)}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-pictogram-name')} {`\n`}
              <strong>
                {mapPictogramText(
                  ivim?.pictogramNature,
                  ivim?.pictogramSerialNumber,
                  ivim?.serviceCategoryCode,
                  ivim?.serviceSubCategoryCode,
                )}
              </strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-source')} {`\n`}
              <strong>
                {ivim.source === 'datexii'
                  ? l10n.getString('forms-event-source-datexii')
                  : ivim.source}
              </strong>
            </Typography>
            {ivim.metadata.connectedDenms && (
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-ivim-connected-denms')} {`\n`}
                <strong>
                  {ivim.metadata.connectedDenms.map((denm) => denm.emerasId).join(', ')}
                </strong>
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            {ivim.metadata?.rsus && (
              <div style={{ marginTop: '20px' }}>
                <Accordion title={l10n.getString('view-selected-devices')}>
                  <RsusMetaData rsus={ivim.metadata.rsus} />
                </Accordion>
              </div>
            )}
            <AuditHistorySection
              key={ivim.id}
              resourceType={AuditResourceType.IVIM}
              resourceId={ivim.id}
            />
          </Grid>
        </Grid>

        {showButtons && (
          <Grid item xs={12} style={{ paddingLeft: 0, marginTop: 10 }}>
            {(isActive || isDisabled) && (
              <>
                {!isDisabled && (
                  <RoleGuard
                    allowed={writeAllowed}
                    blockedActionMode={blockedActionMode}
                    blockedMessage={blockedMessage}
                  >
                    <Tooltip title={l10n.getString('forms-event-update-btn')}>
                      <FontAwesomeIcon
                        icon={faPen}
                        style={{
                          backgroundColor: blue[400],
                        }}
                        className={classes.iconButton}
                        size="xs"
                        onClick={onUpdate}
                      />
                    </Tooltip>
                  </RoleGuard>
                )}
                <RoleGuard
                  allowed={writeAllowed}
                  blockedActionMode={blockedActionMode}
                  blockedMessage={blockedMessage}
                >
                  <Tooltip
                    title={l10n.getString(
                      isDisabled ? 'forms-event-enable-btn' : 'forms-event-disable-btn',
                    )}
                  >
                    <FontAwesomeIcon
                      icon={isDisabled ? faEye : faEyeSlash}
                      style={{
                        backgroundColor: blue[400],
                      }}
                      className={classes.iconButton}
                      size="xs"
                      onClick={isDisabled ? onEnable : onDisable}
                    />
                  </Tooltip>
                </RoleGuard>
              </>
            )}
            {!isActive && !isDisabled && (
              <RoleGuard
                allowed={writeAllowed}
                blockedActionMode={blockedActionMode}
                blockedMessage={blockedMessage}
              >
                <Tooltip title={l10n.getString('forms-event-clone-btn')}>
                  <FontAwesomeIcon
                    icon={faClone}
                    style={{
                      backgroundColor: blue[400],
                    }}
                    className={classes.iconButton}
                    size="xs"
                    onClick={onClone}
                  />
                </Tooltip>
              </RoleGuard>
            )}
            {(isActive || isDisabled) && (
              <RoleGuard
                allowed={writeAllowed}
                blockedActionMode={blockedActionMode}
                blockedMessage={blockedMessage}
              >
                <Tooltip title={l10n.getString('forms-event-delete-btn')}>
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{
                      backgroundColor: red[400],
                    }}
                    className={classes.iconButton}
                    size="xs"
                    onClick={onDelete}
                  />
                </Tooltip>
              </RoleGuard>
            )}
          </Grid>
        )}
      </Grid>
    </CardContent>
  );
};

export default React.memo(CardDetails);
